import {CaretDown, CircleNotch} from '@phosphor-icons/react';
import {array, bool, func, number, oneOfType, string} from 'prop-types';
import {useCallback, useEffect, useMemo, useRef} from 'react';
import {FixedSizeList as List} from 'react-window';

import {isNum} from '../../../../../lib/math.mjs';
import Ping from './Ping.jsx';
import TargetSelectRow from './TargetSelectRow.jsx';

export default function TargetSelect({
  value,
  target,
  options = [],
  onChange,
  placeholder = 'ENTER',
  prevTarget,
  isLoading,
  showIndicator,
  isActive,
}) {
  const listRef = useRef();

  const handleChange = useCallback(
    value => {
      onChange(value);
      const elem = document.activeElement;
      // This is how we close the menu overlay in daisy
      if (elem) {
        elem?.blur();
      }
    },
    [onChange]
  );

  const valueIndex = useMemo(() => {
    return options.indexOf(value);
  }, [options, value]);

  useEffect(() => {
    if (valueIndex !== -1) {
      listRef.current?.scrollToItem(valueIndex + 2);
    }
  }, [valueIndex]);

  // Handle scroll positions
  useEffect(() => {
    if (!isNum(value)) {
      if (isNum(target) && target > 0) {
        const targetIndex = options.indexOf(prevTarget);
        if (targetIndex !== -1) {
          listRef.current.scrollToItem(targetIndex, 'center');
        }
      } else if (prevTarget) {
        const prevTargetIndex = options.indexOf(prevTarget);
        if (prevTargetIndex !== -1) {
          listRef.current.scrollToItem(prevTargetIndex, 'end');
        }
      }
    }
  }, [options, prevTarget, target, value]);

  return (
    <div
      className={`dropdown dropdown-bottom relative flex h-[32px] w-[88px] items-center border bg-base-200/30 focus:outline-none dark:bg-base-content/10 ${
        isActive ? 'border-accent' : 'border-base-300/30 dark:border-base-100'
      }`}
    >
      <label
        tabIndex={0}
        className={`flex w-full cursor-pointer justify-center px-2 text-base-content sm:text-sm`}
      >
        <div className="flex w-4 flex-1 justify-center text-center font-mono text-sm font-medium">
          {isLoading ? (
            <CircleNotch size={18} className="animate-spin text-gray-400" />
          ) : isNum(value) && value >= 0 ? (
            value
          ) : (
            placeholder
          )}
        </div>

        <div className="pointer-events-none inset-y-0 flex items-center">
          <CaretDown size={16} className="text-base-content" />
        </div>
      </label>
      <div
        tabIndex={0}
        className="dropdown-content z-10 ml-[-1px] mt-0.5 box-border w-[88px] border border-base-300/30 bg-base-100 shadow-lg dark:border-base-100/30"
      >
        <List
          ref={listRef}
          height={175}
          itemCount={options.length}
          itemSize={35}
          itemData={{options, value, placeholder, target}}
        >
          {props => <TargetSelectRow {...props} onChange={handleChange} />}
        </List>
      </div>
      {showIndicator && <Ping />}
    </div>
  );
}

TargetSelect.propTypes = {
  value: oneOfType([number, string]),
  target: oneOfType([number, string]),
  options: array,
  onChange: func.isRequired,
  placeholder: string,
  prevTarget: oneOfType([number, string]),
  isLoading: bool.isRequired,
  isActive: bool.isRequired,
  showIndicator: bool,
};
